import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  Tag, 
  Trash2, 
  Share
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import { Button } from '../components/common/Button';
import { ParticleBackground } from '../components/ParticleBackground';
import { useToast } from '../components/common/Toast';
import { withPageTransition, PageTransitionItem } from '../utils/withTransition';
import careerService, { DomainUpdate } from '../services/careerService';
import CodeBlock from '../components/common/CodeBlock';
import '../styles/markdown.css';

// Custom background gradient styles
const backgroundGradient = {
  background: 'radial-gradient(circle at 10% 20%, rgba(90, 92, 106, 0.05) 0%, rgba(32, 45, 58, 0.01) 81.3%)',
  position: 'relative' as 'relative',
  zIndex: 0,
  overflow: 'hidden' as 'hidden'
};

const DomainUpdateDetailsPage: React.FC = () => {
  const [update, setUpdate] = useState<DomainUpdate | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  
  const { updateId } = useParams<{ updateId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Fetch update details
  useEffect(() => {
    const fetchUpdateDetails = async () => {
      if (!updateId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch the specific update by ID
        const updates = await careerService.getDomainUpdates();
        const foundUpdate = updates.find(u => u._id === updateId);
        
        if (foundUpdate) {
          setUpdate(foundUpdate);
          
          // Mark as read if not already
          if (!foundUpdate.read) {
            await careerService.markDomainUpdateAsRead(updateId);
          }
        } else {
          setError('Update not found');
        }
      } catch (err) {
        console.error('Error fetching domain update details:', err);
        setError('Failed to load update details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUpdateDetails();
  }, [updateId]);

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  // Handle share button click
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    showToast?.('Link copied to clipboard', 'success');
  };

  // Handle delete update
  const handleDeleteUpdate = async () => {
    if (!updateId) return;
    
    try {
      setIsDeleting(true);
      const success = await careerService.deleteDomainUpdate(updateId);
      
      if (success) {
        showToast?.('Update deleted successfully', 'success');
        navigate('/dashboard');
      } else {
        showToast?.('Failed to delete update', 'error');
      }
    } catch (err) {
      console.error('Error deleting update:', err);
      showToast?.('Failed to delete update', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark" style={backgroundGradient}>
        <ParticleBackground />
        <div className="text-center bg-background-light/50 dark:bg-background-dark/50 p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm">
          <div className="animate-spin w-10 h-10 border-4 border-primary-light border-t-transparent rounded-full mb-4 mx-auto"></div>
          <p className="text-text-secondary-light dark:text-text-secondary-dark">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error || !update) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
        <ParticleBackground />
        <div className="container mx-auto px-4 py-24 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-heading font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
              Update Not Found
            </h2>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
              We couldn't find the update you're looking for.
            </p>
            {/* Back to dashboard button - centered */}
            <div className="flex justify-center">
              <Button 
                variant="outline" 
                onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-text-primary-light dark:text-text-primary-dark" style={backgroundGradient}>
      {/* Animated gradient elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
        <div className="absolute top-[10%] left-[5%] w-[40%] h-[30%] rounded-full bg-primary-light/5 dark:bg-primary-dark/5 blur-[80px] animate-pulse-slow"></div>
        <div className="absolute bottom-[20%] right-[10%] w-[25%] h-[40%] rounded-full bg-secondary-light/5 dark:bg-secondary-dark/5 blur-[80px] animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-[40%] right-[30%] w-[20%] h-[20%] rounded-full bg-primary-light/3 dark:bg-primary-dark/3 blur-[80px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>
      
      <ParticleBackground />
      
      <main className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Back link */}
          <PageTransitionItem index={1}>
            <button 
              onClick={() => navigate('/dashboard')}
              className="mb-6 flex items-center text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            >
              <ArrowLeft size={16} className="mr-2" />
              <span className="text-base">Back to Dashboard</span>
            </button>
          </PageTransitionItem>
          
          {/* Header */}
          <PageTransitionItem index={2}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-4 md:mb-0">
                {update.title}
              </h1>
              
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleShare}
                  leftIcon={<Share size={16} />}
                >
                  Share
                </Button>
                
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDeleteUpdate}
                  loading={isDeleting}
                  disabled={isDeleting}
                  className="hover:bg-red-500/10 dark:hover:bg-red-500/10 !border-red-500/20 dark:!border-red-500/20 !text-red-500 dark:!text-red-500"
                  leftIcon={<Trash2 size={16} />}
                >
                  Delete
                </Button>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* Meta information */}
          <PageTransitionItem index={3}>
            <div className="flex flex-wrap gap-3 mb-8">
              <div className="inline-flex items-center text-sm bg-primary-light/10 dark:bg-primary-dark/10 px-3 py-1 rounded-full border border-primary-light/20 dark:border-primary-dark/20">
                <Calendar size={14} className="mr-1.5 text-primary-light dark:text-primary-dark" />
                <span className="text-sm text-text-primary-light dark:text-text-primary-dark font-medium">
                  {formatDate(update.updatedAt)}
                </span>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* Tags */}
          <PageTransitionItem index={4}>
            <div className="flex flex-wrap gap-2 mb-8">
              {update.tags?.map((tag, index) => (
                <span key={index} className="inline-flex items-center text-xs bg-secondary-light/10 dark:bg-secondary-dark/10 px-3 py-1 rounded-full">
                  <Tag size={14} className="mr-1 text-secondary-light dark:text-secondary-dark" />
                  <span className="text-sm text-text-primary-light dark:text-text-primary-dark font-medium">{tag}</span>
                </span>
              ))}
            </div>
          </PageTransitionItem>
          
          {/* Main content */}
          <PageTransitionItem index={5}>
            <div className="bg-background-light/50 dark:bg-background-dark/50 p-4 md:p-6 rounded-xl border border-border-light dark:border-border-dark backdrop-blur-sm mb-8">
              <div className="prose prose-sm dark:prose-invert max-w-none markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw, rehypeHighlight]}
                  components={{
                    code({node, inline, className, children, ...props}: any) {
                      const match = /language-(\w+)/.exec(className || '');
                      return !inline && match ? (
                        <CodeBlock
                          language={match[1]}
                          value={String(children).replace(/\n$/, '')}
                        />
                      ) : (
                        <code className={className} {...props}>
                          {children}
                        </code>
                      );
                    }
                  }}
                >
                  {update.content}
                </ReactMarkdown>
              </div>
            </div>
          </PageTransitionItem>
          
          {/* Bottom actions */}
          <PageTransitionItem index={6}>
            <div className="flex justify-end">
            </div>
          </PageTransitionItem>
        </div>
      </main>
    </div>
  );
};

export default withPageTransition(DomainUpdateDetailsPage); 